import React, { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { transactionsApi } from '../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, TrendingUp, TrendingDown, DollarSign, Trash2 } from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
} from 'recharts';

const EXPENSE_CATEGORIES = [
  'Salarios', 'Marketing', 'Software', 'Oficina', 'Viajes', 'Servicios', 'Otro'
];

const INCOME_CATEGORIES = [
  'Pago de Factura', 'Consultoría', 'Tarifa de Proyecto', 'Retainer', 'Otro'
];

const FinancesPage = () => {
  const [transactions, setTransactions] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState({
    transaction_type: 'expense',
    amount: '',
    description: '',
    category: '',
    date: format(new Date(), 'yyyy-MM-dd'),
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [txnsRes, summaryRes] = await Promise.all([
        transactionsApi.getAll(),
        transactionsApi.getSummary(),
      ]);
      setTransactions(txnsRes.data);
      setSummary(summaryRes.data);
    } catch (error) {
      toast.error('Error al cargar datos financieros');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      await transactionsApi.create({
        ...formData,
        amount: parseFloat(formData.amount),
        date: new Date(formData.date).toISOString(),
      });
      toast.success('Transacción agregada');
      setDialogOpen(false);
      setFormData({
        transaction_type: 'expense',
        amount: '',
        description: '',
        category: '',
        date: format(new Date(), 'yyyy-MM-dd'),
      });
      fetchData();
    } catch (error) {
      toast.error('Error al agregar transacción');
    }
  };

  const handleDelete = async (txnId) => {
    try {
      await transactionsApi.delete(txnId);
      toast.success('Transaccion eliminada');
      fetchData();
    } catch (error) {
      toast.error('Error al eliminar transaccion');
    }
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN',
    }).format(amount || 0);
  };

  // Prepare chart data
  const chartData = summary?.monthly_data
    ? Object.entries(summary.monthly_data)
        .sort(([a], [b]) => a.localeCompare(b))
        .map(([month, data]) => ({
          month: format(new Date(month + '-01'), 'MMM', { locale: es }),
          ingresos: data.income,
          gastos: data.expenses,
          ganancia: data.income - data.expenses,
        }))
    : [];

  const categoryData = summary?.categories
    ? Object.entries(summary.categories).map(([name, data]) => ({
        name,
        value: data.expenses + data.income,
        expenses: data.expenses,
        income: data.income,
      }))
    : [];

  const COLORS = ['#F97316', '#0A0A0A', '#FDBA74', '#F59E0B', '#EF4444', '#3B82F6', '#8B5CF6'];

  if (loading) {
    return (
      <DashboardLayout title="Dashboard Financiero">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-orange-500"></div>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Dashboard Financiero">
      <div className="space-y-6" data-testid="finances-page">
        {/* Summary Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-green-500/20">
                  <TrendingUp className="text-green-400" size={24} />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Ingresos Totales</p>
                  <p className="text-2xl font-bold text-white" data-testid="total-income">
                    {formatCurrency(summary?.total_income)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-red-500/20">
                  <TrendingDown className="text-red-400" size={24} />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Gastos Totales</p>
                  <p className="text-2xl font-bold text-white" data-testid="total-expenses">
                    {formatCurrency(summary?.total_expenses)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#141414] border-white/5">
            <CardContent className="p-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-xl bg-orange-500/20">
                  <DollarSign className="text-orange-400" size={24} />
                </div>
                <div>
                  <p className="text-sm text-zinc-400">Ganancia Neta</p>
                  <p className={`text-2xl font-bold ${(summary?.net_profit || 0) >= 0 ? 'text-green-400' : 'text-red-400'}`} data-testid="net-profit">
                    {formatCurrency(summary?.net_profit)}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Charts Row */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Cash Flow Chart */}
          <Card className="lg:col-span-2 bg-[#141414] border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Flujo de Caja</CardTitle>
            </CardHeader>
            <CardContent>
              {chartData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#2A2A2A" />
                      <XAxis dataKey="month" stroke="#71717A" />
                      <YAxis stroke="#71717A" tickFormatter={(v) => `$${v/1000}k`} />
                      <Tooltip
                        contentStyle={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: '8px' }}
                        labelStyle={{ color: '#fff' }}
                        formatter={(value) => formatCurrency(value)}
                      />
                      <Line type="monotone" dataKey="ingresos" stroke="#22C55E" strokeWidth={2} dot={false} name="Ingresos" />
                      <Line type="monotone" dataKey="gastos" stroke="#EF4444" strokeWidth={2} dot={false} name="Gastos" />
                      <Line type="monotone" dataKey="ganancia" stroke="#F97316" strokeWidth={2} dot={false} name="Ganancia" />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center">
                  <p className="text-zinc-400">Sin datos para mostrar</p>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Category Breakdown */}
          <Card className="bg-[#141414] border-white/5">
            <CardHeader>
              <CardTitle className="text-white">Por Categoría</CardTitle>
            </CardHeader>
            <CardContent>
              {categoryData.length > 0 ? (
                <div className="h-[300px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <PieChart>
                      <Pie
                        data={categoryData}
                        cx="50%"
                        cy="50%"
                        innerRadius={60}
                        outerRadius={100}
                        paddingAngle={2}
                        dataKey="value"
                      >
                        {categoryData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={COLORS[index % COLORS.length]} />
                        ))}
                      </Pie>
                      <Tooltip
                        contentStyle={{ background: '#1A1A1A', border: '1px solid #2A2A2A', borderRadius: '8px' }}
                        formatter={(value) => formatCurrency(value)}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              ) : (
                <div className="h-[300px] flex items-center justify-center">
                  <p className="text-zinc-400">Sin datos para mostrar</p>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Transactions */}
        <Card className="bg-[#141414] border-white/5">
          <CardHeader className="flex flex-row items-center justify-between">
            <CardTitle className="text-white">Transacciones Recientes</CardTitle>
            <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
              <DialogTrigger asChild>
                <Button className="bg-orange-500 hover:bg-orange-600" data-testid="add-transaction-btn">
                  <Plus size={20} className="mr-2" />
                  Agregar Transacción
                </Button>
              </DialogTrigger>
              <DialogContent className="bg-[#141414] border-white/10">
                <DialogHeader>
                  <DialogTitle className="text-white">Agregar Transacción</DialogTitle>
                </DialogHeader>
                <form onSubmit={handleSubmit} className="space-y-4 mt-4">
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Tipo</Label>
                    <Select
                      value={formData.transaction_type}
                      onValueChange={(value) => setFormData({ ...formData, transaction_type: value, category: '' })}
                    >
                      <SelectTrigger className="bg-[#1A1A1A] border-transparent">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="income">Ingreso</SelectItem>
                        <SelectItem value="expense">Gasto</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Monto</Label>
                      <Input
                        type="number"
                        value={formData.amount}
                        onChange={(e) => setFormData({ ...formData, amount: e.target.value })}
                        className="bg-[#1A1A1A] border-transparent"
                        placeholder="0.00"
                        min="0"
                        step="0.01"
                        required
                        data-testid="transaction-amount"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label className="text-zinc-300">Fecha</Label>
                      <Input
                        type="date"
                        value={formData.date}
                        onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                        className="bg-[#1A1A1A] border-transparent"
                        required
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Categoría</Label>
                    <Select
                      value={formData.category || "select"}
                      onValueChange={(value) => setFormData({ ...formData, category: value === "select" ? "" : value })}
                    >
                      <SelectTrigger className="bg-[#1A1A1A] border-transparent">
                        <SelectValue placeholder="Seleccionar categoría" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="select" disabled>Seleccionar categoría</SelectItem>
                        {(formData.transaction_type === 'income' ? INCOME_CATEGORIES : EXPENSE_CATEGORIES).map((cat) => (
                          <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label className="text-zinc-300">Descripción</Label>
                    <Input
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      className="bg-[#1A1A1A] border-transparent"
                      placeholder="Descripción de la transacción"
                      required
                      data-testid="transaction-description"
                    />
                  </div>
                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600" data-testid="transaction-submit">
                    Agregar Transacción
                  </Button>
                </form>
              </DialogContent>
            </Dialog>
          </CardHeader>
          <CardContent>
            {transactions.length === 0 ? (
              <div className="text-center py-8">
                <DollarSign className="mx-auto text-zinc-600 mb-4" size={48} />
                <p className="text-zinc-400">Aún no hay transacciones</p>
              </div>
            ) : (
              <div className="space-y-3">
                {transactions.slice(0, 10).map((txn) => (
                  <div
                    key={txn.transaction_id}
                    className="flex items-center justify-between p-4 rounded-lg bg-[#1A1A1A] hover:bg-[#222] transition-colors"
                    data-testid={`transaction-${txn.transaction_id}`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`p-2 rounded-lg ${
                        txn.transaction_type === 'income' ? 'bg-green-500/20' : 'bg-red-500/20'
                      }`}>
                        {txn.transaction_type === 'income' ? (
                          <TrendingUp className="text-green-400" size={20} />
                        ) : (
                          <TrendingDown className="text-red-400" size={20} />
                        )}
                      </div>
                      <div>
                        <p className="font-medium text-white">{txn.description}</p>
                        <p className="text-sm text-zinc-500">{txn.category} • {format(new Date(txn.date), 'd MMM, yyyy', { locale: es })}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`font-medium ${
                        txn.transaction_type === 'income' ? 'text-green-400' : 'text-red-400'
                      }`}>
                        {txn.transaction_type === 'income' ? '+' : '-'}{formatCurrency(txn.amount)}
                      </span>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => handleDelete(txn.transaction_id)}
                        className="text-zinc-500 hover:text-red-400"
                      >
                        <Trash2 size={16} />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </DashboardLayout>
  );
};

export default FinancesPage;
